/**
 * Typed API client. Use `apiCall(op, args)` for one-shot fetches and
 * `useApi(op, args)` for SWR-bound queries. Both accept `Operation`
 * contracts defined in `./contracts/`.
 *
 * Direct `fetch()` outside this file is banned by convention (see
 * CLAUDE.md "Frontend conventions"). The only exception is external
 * third-party APIs (e.g. products/document/lib/llm.ts → Anthropic).
 *
 * This file is `"use client"` because `useApi` calls into SWR's
 * `useSWR` hook, which is React-client-only. The pure helpers
 * `apiCall`/`apiKey`/`defaultFetcher` would work in a server context,
 * but Next's RSC bundler picks SWR's `react-server` entrypoint (which
 * has no default export) the moment a server module touches this file
 * — so we keep the whole module client-only and route handlers import
 * the server helper from `./server.ts` instead.
 */

import useSWR, {
  mutate as globalMutate,
  type ScopedMutator,
  type SWRConfiguration,
  type SWRResponse,
} from "swr";
import {
  ApiError,
  apiKey,
  invalidationPrefixes,
  keyInPrefixFamily,
  type Args,
  type Operation,
  buildUrl,
  envelopeSchema,
} from "./types";

export { ApiError, apiKey, scoped, INVALIDATION_SCOPED, invalidationPrefixes, keyInPrefixFamily } from "./types";
export type { HttpMethod, Operation, Args, Envelope, InvalidationTarget } from "./types";

/**
 * The active cache's bound `mutate` — registered by the app's `<SWRConfig>`
 * root (`excellent/app/_chrome/swr-provider.tsx`, via its `MutateBridge`)
 * as soon as it mounts.
 *
 * `apiCall`'s automatic `Operation.invalidates` wiring below runs outside
 * React (a plain async function), so it can't call the `useSWRConfig()`
 * hook itself — it needs a module-level handle onto whichever `mutate` the
 * mounted app is actually bound to. The top-level `mutate` this file
 * imports from `"swr"` targets SWR's DEFAULT cache; this repo's shell wraps
 * its tree in a custom `provider` (persisted to localStorage), so that
 * default-cache `mutate` is a silent no-op against it there (the exact
 * `favorite-toggle.tsx` bug this task fixes — see the commit). Falling
 * back to the default-cache `mutate` when nothing is registered keeps this
 * correct for any consumer that does NOT mount a custom provider (tests,
 * a bare `<SWRConfig>`-less tree).
 */
let boundMutate: ScopedMutator | null = null;

/** Called by `MutateBridge` on mount/unmount — see the comment above. */
export function registerBoundMutate(fn: ScopedMutator | null): void {
  boundMutate = fn;
}

function invalidate(prefixes: string[]): void {
  if (prefixes.length === 0) return;
  const mutateFn = boundMutate ?? globalMutate;
  void mutateFn(
    (key) => prefixes.some((prefix) => keyInPrefixFamily(key, prefix)),
    undefined,
    { revalidate: true },
  );
}

/** One-shot fetch. Throws ApiError on non-ok envelopes or schema mismatch. */
export async function apiCall<TParams, TQuery, TBody, TResult>(
  op: Operation<TParams, TQuery, TBody, TResult>,
  args: Args<TParams, TQuery, TBody>,
): Promise<TResult> {
  const url = buildUrl(op, args.params as TParams, args.query as TQuery);
  const init: RequestInit = { method: op.method };
  if (args.body !== undefined && args.body !== null) {
    // Multipart / FormData body mode (#44447): the careers apply form posts a
    // résumé file. Leave Content-Type unset so the runtime supplies the
    // multipart boundary; never JSON.stringify a FormData instance.
    if (typeof FormData !== "undefined" && args.body instanceof FormData) {
      init.body = args.body;
    } else {
      init.headers = { "content-type": "application/json" };
      init.body = JSON.stringify(args.body);
    }
  }
  const res = await fetch(url, init);
  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    throw new ApiError(res.status, `non-json response (${res.status}) at ${url}`);
  }
  const parsed = envelopeSchema(op.response).safeParse(raw);
  if (!parsed.success) {
    throw new ApiError(
      res.status,
      `invalid envelope at ${url}: ${parsed.error.message}`,
    );
  }
  if (!parsed.data.ok) {
    throw new ApiError(res.ok ? 500 : res.status, parsed.data.error);
  }
  // Declarative cache invalidation (RO-5-01) — a successful mutation
  // refetches every read its contract names in `invalidates`, family-scoped
  // (see InvalidationTarget). A `GET`'s own `invalidates` (there shouldn't
  // be one) is never acted on.
  if (op.method !== "GET" && op.invalidates?.length) {
    invalidate(invalidationPrefixes(op.invalidates, args, parsed.data.data));
  }
  return parsed.data.data;
}

/**
 * SWR-bound query for read operations. Pass `null` for args to skip the call.
 *
 * Cache key is the resolved URL — same key matches both `apiCall` and `useApi`,
 * so a successful POST that returns the same payload can `mutate(apiKey(op, args), data)`.
 */
export function useApi<TParams, TQuery, TBody, TResult>(
  op: Operation<TParams, TQuery, TBody, TResult>,
  args: Args<TParams, TQuery, TBody> | null,
  swr?: SWRConfiguration<TResult>,
): SWRResponse<TResult> {
  const key = args === null ? null : apiKey(op, args);
  return useSWR<TResult>(
    key,
    () => apiCall(op, args as Args<TParams, TQuery, TBody>),
    swr,
  );
}

/**
 * Default fetcher for SWR's global config. Strict envelope mode:
 * the response MUST be `{ ok: true, data } | { ok: false, error }` —
 * i.e. produced by `createRoute(...)`. Non-enveloped responses throw.
 *
 * This used to tolerate bare JSON for the legacy `useSWR(url)` migration;
 * that migration is complete (all read endpoints now flow through
 * `useApi` against a registered operation), so the fallback is gone.
 *
 * Direct `useSWR(url)` calls are still supported for the rare case where
 * a key is needed but the consumer wants its own typed view of the data
 * — but the route MUST go through `createRoute`.
 */
export async function defaultFetcher(url: string): Promise<unknown> {
  const res = await fetch(url);
  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    throw new ApiError(res.status, `non-json response (${res.status}) at ${url}`);
  }
  if (!raw || typeof raw !== "object" || !("ok" in raw)) {
    throw new ApiError(
      res.status,
      `response at ${url} is missing the standard envelope. Either route the endpoint through createRoute() or call it via apiCall/useApi against a typed Operation.`,
    );
  }
  const e = raw as { ok: unknown; data?: unknown; error?: unknown };
  if (e.ok === true) return e.data;
  const err = typeof e.error === "string" ? e.error : `API error ${res.status}`;
  throw new ApiError(res.ok ? 500 : res.status, err);
}

// Streaming client (docs/copilot/PRD.md §4.3) — re-exported so product code
// reaches the streaming seam through `@excellent/ui/api`, never raw `fetch`.
export { useStream } from "./client-stream";
export type { StreamStatus, UseStreamResult } from "./client-stream";

// List-pagination convention (docs/design/LIST-PAGINATION-AUDIT.md):
//  - usePagedList: URL-driven offset pages (pairs with paginatedSchema + TablePager)
//  - useCursorList: keyset "load more" feeds (pairs with keysetSchema)
export { usePagedList } from "./use-paged-list";
export type {
  UsePagedListOptions,
  UsePagedListResult,
  PagerProps,
} from "./use-paged-list";
export { useCursorList } from "./use-cursor-list";
export type {
  UseCursorListOptions,
  UseCursorListResult,
} from "./use-cursor-list";

// Shared create-modal attachment helper — upload + link a batch of files to a
// just-created entity through the one attachments endpoint (DRY across flows).
export { attachFilesToEntity } from "./attach";
